<template>
  <Card class="cost-allocation-order-cost-card">
    <template #content>
      <Section
        :id="id"
        :header="
          t('shipments.financials.orderCostAllocations', shipmentIds.length)
        "
        class="order-cost-section p-0"
        data-class="flex flex-column flex-gap-1 p-0"
      >
        <template #headerRight>
          <RygenButton
            mode="secondary"
            icon="pi pi-refresh"
            :label="t('common.refresh')"
            :disabled="shipmentRateAllocations.loading"
            @click="getShipmentRateAllocationsForOrder()"
          />
        </template>

        <h3 class="font-medium">{{ t('shipmentFinancials.runningLedger') }}</h3>

        <CostAllocationOrderCostTable
          :shipment-ids="shipmentIds"
          :shipment-rate-allocations-by-shipment="shipmentRateAllocations.data"
          :loading="shipmentRateAllocations.loading"
          :billing-currency="billingCurrency"
        />
      </Section>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, PropType, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast, useUid } from '@rygen/rygen-ui/composables';
import { Currency, Loadable } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { Shipment } from '@/model';
import { ShipmentId, ShipmentRateAllocation } from '@/model/shipmentFinancials';

import CostAllocationOrderCostTable from '@/components/shipmentFinancials/costAllocation/CostAllocationOrderCostTable.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Card from 'primevue/card';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('cost-allocation-order-section').uid,
  },
  orderId: {
    type: Number,
    required: true,
  },
  shipments: {
    type: Array as PropType<Shipment[]>,
    default: () => [],
  },
});
const { id, orderId, shipments } = toRefs(props);

const { t } = useI18n();
const toast = useToast();

const shipmentRateAllocations: Ref<
  Loadable<Map<ShipmentId, ShipmentRateAllocation>>
> = ref({
  data: new Map(),
  loading: false,
});

const billingCurrency = computed(
  () =>
    shipments.value.at(0)?.financialSummary.costEstimate?.currency ??
    Currency.USD,
);

const shipmentIds = computed(() =>
  shipments.value.map((shipment) => shipment.id).filter(notNull),
);

const getShipmentRateAllocationsForOrder = async () => {
  try {
    shipmentRateAllocations.value.data?.clear();
    shipmentRateAllocations.value.loading = true;

    const rateAllocations =
      await api.shipmentFinancials.getShipmentRateAllocationsOrderBatch(
        shipmentIds.value,
        orderId.value,
      );

    rateAllocations.forEach((rateAllocation) => {
      const shipmentId = rateAllocation.shipment?.sourceSystemId;

      if (shipmentId) {
        shipmentRateAllocations.value.data?.set(shipmentId, rateAllocation);
      }
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('costAllocation.costAllocation'),
      }),
      error: e,
    });
    console.error(e);
  } finally {
    shipmentRateAllocations.value.loading = false;
  }
};

watch(
  shipments,
  async (newVal: Shipment[]) => {
    if (newVal.length > 0) {
      await getShipmentRateAllocationsForOrder();
    }
  },
  { immediate: true },
);
</script>

<style>
.cost-allocation-order-cost-card.p-card {
  & > .p-card-body {
    padding: 0;

    .order-cost-section {
      .section-header {
        padding: var(--sp-6) var(--sp-6) 0;
      }

      .section-data {
        border-top: var(--divider);

        h3 {
          padding: var(--sp-3) var(--sp-3) var(--sp-3) var(--sp-6);
        }
      }
    }
  }
}
</style>
